import { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { ActivityGranularity, ACTIVITY_GRANULARITIES } from '@/models/IActivity'
import { computeDelta, GRANULARITY_LABELS } from '@/utils/activity'

export const Pulse = ({ className = 'h-4 w-24' }: { className?: string }) => (
  <div className={cn('animate-pulse rounded-md bg-muted/60', className)} />
)

type ActivityPanelProps = {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
}

/** card surface used by all activity sections */
export const ActivityPanel = ({
  title,
  subtitle,
  actions,
  children,
  className
}: ActivityPanelProps) => (
  <section
    className={cn(
      'space-y-4 rounded-lg bg-muted/30 p-4 text-foreground',
      className
    )}
  >
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 space-y-0.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        {subtitle && (
          <p className="text-xs text-muted-foreground/70">{subtitle}</p>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-3">{actions}</div>
      )}
    </header>
    {children}
  </section>
)

type KpiTileProps = {
  label: ReactNode
  value: ReactNode
  /** rendered next to the value, e.g. `39 / 45 paid` */
  suffix?: ReactNode
  hint?: ReactNode
  isLoading?: boolean
  indicator?: 'live'
  tone?: 'default' | 'warning'
}

export const KpiTile = ({
  label,
  value,
  suffix,
  hint,
  isLoading,
  indicator,
  tone = 'default'
}: KpiTileProps) => (
  <div className="space-y-2 rounded-lg bg-muted/30 p-4 text-foreground">
    <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground/80">
      {indicator === 'live' && (
        <span aria-hidden className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
        </span>
      )}
      {label}
    </div>
    {isLoading ? (
      <Pulse className="h-8 w-20" />
    ) : (
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span
          className={cn(
            'text-3xl font-semibold tabular-nums',
            tone === 'warning' && 'text-amber-400'
          )}
        >
          {value}
        </span>
        {suffix && (
          <span className="text-sm text-muted-foreground/80">{suffix}</span>
        )}
      </div>
    )}
    {hint && (
      <div className="text-xs text-muted-foreground/70">
        {isLoading ? <Pulse className="h-3 w-32" /> : hint}
      </div>
    )}
  </div>
)

type DeltaTextProps = {
  current: number
  previous: number
  /** e.g. `vs last week` */
  suffix?: string
  className?: string
}

/** `▲ 6% vs last week (32)` */
export const DeltaText = ({
  current,
  previous,
  suffix,
  className
}: DeltaTextProps) => {
  const delta = computeDelta(current, previous)

  return (
    <span className={cn('tabular-nums', className)}>
      <span
        className={cn(
          'font-semibold',
          delta.direction === 'up' && 'text-emerald-400',
          delta.direction === 'down' && 'text-rose-400'
        )}
      >
        {delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : ''}{' '}
        {delta.label}
      </span>
      {suffix && (
        <span className="text-muted-foreground/70">
          {' '}
          {suffix} ({previous})
        </span>
      )}
    </span>
  )
}

type SegmentedOption<T extends string> = { value: T; label: string }

type SegmentedProps<T extends string> = {
  value: T
  options: SegmentedOption<T>[]
  onChange: (value: T) => void
  ariaLabel: string
  size?: 'sm' | 'md'
}

export const Segmented = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  size = 'md'
}: SegmentedProps<T>) => (
  <div
    aria-label={ariaLabel}
    className="inline-flex rounded-md bg-muted/40 p-0.5 text-foreground"
    role="radiogroup"
  >
    {options.map((option) => (
      <button
        aria-checked={option.value === value}
        className={cn(
          'rounded px-3 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          size === 'sm' ? 'py-0.5 text-xs' : 'py-1 text-sm',
          option.value === value
            ? 'bg-muted text-foreground shadow-sm'
            : 'text-muted-foreground/70 hover:text-foreground'
        )}
        key={option.value}
        onClick={() => onChange(option.value)}
        role="radio"
        type="button"
      >
        {option.label}
      </button>
    ))}
  </div>
)

export const GranularitySwitch = ({
  value,
  onChange
}: {
  value: ActivityGranularity
  onChange: (value: ActivityGranularity) => void
}) => (
  <Segmented
    ariaLabel="Granularity"
    onChange={onChange}
    options={ACTIVITY_GRANULARITIES.map((g) => ({
      value: g,
      label: GRANULARITY_LABELS[g]
    }))}
    value={value}
  />
)

export const parseGranularity = (
  value: string | null,
  fallback: ActivityGranularity = 'week'
): ActivityGranularity =>
  ACTIVITY_GRANULARITIES.includes(value as ActivityGranularity)
    ? (value as ActivityGranularity)
    : fallback

export const ErrorNote = ({ children }: { children: ReactNode }) => (
  <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
    {children}
  </p>
)
